import { useEffect, useState, type ReactNode } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { Alert } from "../ds";
import { alpha } from "@mui/material/styles";
import { useSnackbar } from "notistack";
import {
  ArrowLeft,
  Check,
  CheckCircle,
  Copy,
  Lightbulb,
  Ticket,
  User,
} from "lucide-react";
import { RavenIcon } from "@ravenappdev/raven-ui";
import { Autocomplete, TextField, Select, Chip } from "../ds";
import AttachmentUploader, {
  type UploadedFile,
} from "../components/AttachmentUploader";
import { DRAWER_WIDTH } from "../components/AppLayout";
import { createSuggestion, invalidateSuggestionCaches } from "../api/ess";
import { displayDepartment } from "../api/departments";
import { CATEGORIES, isOtherCategory } from "../types/ess";
import { useDepartmentNames } from "../hooks/useDepartments";
import { buildDepartmentSelectOptions } from "../utils/departments";
import { apiErrorMessage } from "../utils/apiError";
import { useCurrentUserProfile } from "../hooks/useUserProfile";
import { useUserSearch } from "../hooks/useUserSearch";
import { useAuth } from "../context/AuthContext";
import type { UserSearchResult } from "../types/auth";

interface FormValues {
  employee_name: string;
  employee_id: string;
  department: string;
  title: string;
  description: string;
  category: string;
  other_category?: string;
  submitted_on_behalf_of?: string;
}

const schema = yup.object({
  employee_name: yup.string().required("Required").max(255),
  employee_id: yup.string().required("Required").max(100),
  department: yup.string().required("Required").max(255),
  title: yup.string().required("Required").max(500),
  description: yup.string().required("Required"),
  category: yup.string().required("Pick a category"),
  // Free text is mandatory only when "Others" is picked; otherwise it is
  // stripped from the payload (see the mutation below).
  other_category: yup
    .string()
    .max(100)
    .when("category", {
      is: (c: string) => isOtherCategory(c),
      then: (s) => s.trim().required("Please specify the category"),
      otherwise: (s) => s.optional(),
    }),
  submitted_on_behalf_of: yup.string().max(255).optional(),
});

function SectionHeader({
  icon,
  title,
}: {
  icon: ReactNode;
  title: string;
}) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.25} mb={2.5}>
      <Box
        sx={(theme) => ({
          width: 28,
          height: 28,
          bgcolor: alpha(theme.palette.primary.main, 0.12),
          borderRadius: "8px",
          display: "grid",
          placeItems: "center",
          color: theme.palette.primary.main,
        })}
      >
        {icon}
      </Box>
      <Typography
        sx={(theme) => ({
          fontSize: 16,
          fontWeight: 600,
          color: theme.palette.primary.main,
          letterSpacing: "0.15px",
        })}
      >
        {title}
      </Typography>
    </Stack>
  );
}

function SubSectionLabel({ children }: { children: ReactNode }) {
  return (
    <Typography variant="overline" color="text.secondary">
      {children}
    </Typography>
  );
}

function ReadOnlyField({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 0.5, textTransform: "uppercase", letterSpacing: "0.08em" }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 15, color: "text.primary", fontWeight: 500 }}>
        {value || "—"}
      </Typography>
    </Box>
  );
}

export default function NewSuggestion() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { auth } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [attachments, setAttachments] = useState<UploadedFile[]>([]);
  const { departments: departmentList, isLoading: loadingDepartments } =
    useDepartmentNames();
  const profileQuery = useCurrentUserProfile();
  const profile = profileQuery.data;
  // The submitter's own department(s), shown read-only so they can be
  // told apart from the editable "Suggestion Department" below. A user
  // may belong to more than one department, so this is a list.
  const userDepartments = (profile?.user_info?.departments ?? []).filter(
    Boolean,
  ) as string[];
  const [behalfQuery, setBehalfQuery] = useState("");
  const [behalfChoice, setBehalfChoice] =
    useState<UserSearchResult | null>(null);
  const { results: userResults, loading: searchingUsers } =
    useUserSearch(behalfQuery);

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting, isValid },
    reset,
    setValue,
  } = useForm<FormValues>({
    mode: "onChange",
    resolver: yupResolver(schema),
    defaultValues: {
      employee_name: auth?.user.name ?? "",
      employee_id: auth?.user.user_info?.employee_id ?? "",
      department: "",
      title: "",
      description: "",
      category: "",
      other_category: "",
      submitted_on_behalf_of: "",
    },
  });

  const employeeName = watch("employee_name");
  const employeeId = watch("employee_id");
  const category = watch("category");
  const showOtherCategory = isOtherCategory(category);

  // Leaving "Others" drops the free text so a stale label can't ride
  // along (the backend ignores it anyway) and the validity re-evaluates.
  useEffect(() => {
    if (!showOtherCategory) setValue("other_category", "", { shouldValidate: true });
  }, [showOtherCategory, setValue]);

  useEffect(() => {
    if (!profile) return;
    const eid = profile.user_info?.employee_id;
    if (eid) setValue("employee_id", eid, { shouldValidate: true });
    const name = profile.name;
    if (name) setValue("employee_name", name, { shouldValidate: true });
    // Department is user-selected, not pre-filled from profile
  }, [profile, setValue]);

  const [created, setCreated] = useState<{
    id: string;
    number: string | null;
  } | null>(null);

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      createSuggestion({
        ...values,
        other_category: isOtherCategory(values.category)
          ? values.other_category?.trim() || undefined
          : undefined,
        submitted_on_behalf_of: values.submitted_on_behalf_of || undefined,
        // Link the beneficiary to their account, not just their name.
        // The picker below nulls `behalfChoice` whenever the input stops
        // matching the selection, so the id can never be sent for a name
        // it doesn't belong to.
        submitted_on_behalf_of_user_id: values.submitted_on_behalf_of
          ? behalfChoice?.id
          : undefined,
        attachment_ids: attachments.map((a) => a.asset_id),
      }),
    onSuccess: (data) => {
      enqueueSnackbar("Suggestion submitted", { variant: "success" });
      // Invalidate list + stats caches so the sidebar count, Suggestions
      // list, and Overview tiles all refresh in the background. Without
      // this the new suggestion stays invisible until the cached
      // queries hit their staleTime (60s) or the user navigates to a
      // page that owns the query.
      invalidateSuggestionCaches(qc);
      setCreated({ id: data.id, number: data.suggestion_number });
    },
    onError: (err: unknown) => {
      enqueueSnackbar(apiErrorMessage(err, "Submission failed"), {
        variant: "error",
      });
    },
  });

  const onSubmit = handleSubmit((values) => mutation.mutate(values));

  // Task 15 (ESS Procedure-Alignment): Departmental Heads and above are
  // not eligible to originate a suggestion (IEPL-CORP-2.207 §2.0). The
  // sidebar/top-bar affordances are already hidden for these users
  // (AppLayout.tsx), but this page is still reachable by a direct URL —
  // this full-card replacement is the deep-link protection. Placed
  // after every hook call above so hook order stays stable regardless
  // of the gate's outcome. Only an explicit `false` blocks; absent
  // (old backend, or not yet loaded) tolerates as eligible, matching
  // AppLayout's gate. The server remains authoritative (403 on create)
  // — this is UX only.
  if (profile?.can_submit === false) {
    return (
      <Box sx={{ maxWidth: 920, mx: "auto" }}>
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Alert severity="warning">
              Departmental Heads and above are not eligible to submit
              suggestions under IEPL-CORP-2.207.
            </Alert>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (created) {
    return (
      <Box sx={{ maxWidth: 520, mx: "auto" }}>
        <Card
          sx={{
            borderRadius: "24px",
            p: 3,
            textAlign: "center",
          }}
        >
          <Box
            sx={(theme) => ({
              width: 64,
              height: 64,
              borderRadius: "50%",
              bgcolor: alpha(theme.palette.success.main, 0.12),
              display: "grid",
              placeItems: "center",
              mx: "auto",
              mb: 2.5,
              color: theme.palette.success.main,
            })}
          >
            <RavenIcon icon={CheckCircle} fontSize="large" strokeWidth={2} />
          </Box>
          <Typography variant="h5" sx={{ mb: 0.5 }}>
            Suggestion Submitted Successfully
          </Typography>
          <Typography color="text.secondary" mb={3}>
            HR custodians have been notified.
          </Typography>
          {created.number && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1.5,
                mb: 3,
                px: 2,
                py: 1.5,
                borderRadius: "12px",
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.default",
                textAlign: "left",
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.25}>
                <Box
                  component="span"
                  sx={{ color: "text.secondary", display: "inline-flex" }}
                >
                  <RavenIcon icon={Ticket} />
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", lineHeight: 1.2 }}
                  >
                    Your suggestion number
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 16,
                      fontWeight: 700,
                      fontVariantNumeric: "tabular-nums",
                      color: "text.primary",
                      lineHeight: 1.2,
                    }}
                  >
                    {created.number}
                  </Typography>
                </Box>
              </Stack>
              <Tooltip title="Copy to clipboard">
                <IconButton
                  size="small"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(created.number!);
                      enqueueSnackbar("Suggestion number copied", {
                        variant: "success",
                      });
                    } catch {
                      enqueueSnackbar("Couldn't copy — please copy manually", {
                        variant: "warning",
                      });
                    }
                  }}
                  aria-label="Copy suggestion number to clipboard"
                >
                  <RavenIcon icon={Copy} sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          <Stack spacing={1.5} alignItems="center">
            <Stack direction="row" spacing={1.5} justifyContent="center">
              <Button
                variant="contained"
                onClick={() => navigate(`/suggestions/${created.id}`)}
                size="medium"
              >
                Open record
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  reset();
                  setAttachments([]);
                  setBehalfChoice(null);
                  setBehalfQuery("");
                  setCreated(null);
                }}
                size="medium"
              >
                Submit another
              </Button>
            </Stack>
            <Button
              startIcon={<RavenIcon icon={ArrowLeft} />}
              onClick={() => navigate("/suggestions")}
              size="medium"
            >
              Back to list
            </Button>
          </Stack>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 920, mx: "auto" }}>
      <form onSubmit={onSubmit}>
        {/* Submitter Information */}
        <Card sx={{ mb: 2.5 }}>
          <CardContent sx={{ p: 3 }}>
            <SectionHeader
              icon={<RavenIcon icon={User} sx={{ fontSize: 18 }} />}
              title="Submitter Information"
            />
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <ReadOnlyField
                  label="Employee Name"
                  value={employeeName}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <ReadOnlyField label="Employee ID" value={employeeId} />
              </Grid>
              <Grid size={12}>
                <Typography
                  sx={{
                    fontSize: 11,
                    color: "text.secondary",
                    mb: 0.75,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                  }}
                >
                  Your Department
                </Typography>
                {userDepartments.length > 0 ? (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {userDepartments.map((d) => (
                      <Chip
                        key={d}
                        label={displayDepartment(d)}
                        colorVariant="primary"
                        chipVariant="outlined"
                      />
                    ))}
                  </Stack>
                ) : (
                  <Typography sx={{ fontSize: 14, color: "text.disabled" }}>
                    Not set
                  </Typography>
                )}
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="department"
                  control={control}
                  render={({ field }) => (
                    <Select
                      label={
                        loadingDepartments
                          ? "Loading departments…"
                          : "Suggestion Department *"
                      }
                      value={field.value}
                      onChange={field.onChange}
                      options={buildDepartmentSelectOptions(departmentList)}
                      error={!!errors.department}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="category"
                  control={control}
                  render={({ field }) => (
                    <Select
                      label="Category *"
                      value={field.value}
                      onChange={field.onChange}
                      options={CATEGORIES.map((c) => ({
                        label: c,
                        value: c,
                      }))}
                      error={!!errors.category}
                    />
                  )}
                />
              </Grid>
              {showOtherCategory && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="other_category"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Specify category *"
                        placeholder="e.g. Ergonomics"
                        slotProps={{ htmlInput: { maxLength: 100 } }}
                        error={!!errors.other_category}
                        helperText={errors.other_category?.message}
                      />
                    )}
                  />
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>

        {/* Suggestion Details */}
        <Card sx={{ mb: 2.5 }} data-tour="new-suggestion-form">
          <CardContent sx={{ p: 3 }}>
            <SectionHeader
              icon={<RavenIcon icon={Lightbulb} sx={{ fontSize: 18 }} />}
              title="Suggestion Details"
            />

            <Stack spacing={2}>
              <Controller
                name="title"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Suggestion Title *"
                    error={!!errors.title}
                    helperText={errors.title?.message}
                  />
                )}
              />

              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Description *"
                    multiline
                    minRows={4}
                    error={!!errors.description}
                    helperText={errors.description?.message}
                  />
                )}
              />

              <Box sx={{ width: { xs: "100%", sm: "calc(50% - 8px)" } }}>
                <Controller
                  name="submitted_on_behalf_of"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete<UserSearchResult>
                      label="Submitted on behalf of (optional)"
                      placeholder="Search regular employee by name or Emp ID"
                      value={behalfChoice}
                      inputValue={behalfQuery}
                      options={userResults}
                      loading={searchingUsers}
                      noOptionsText={
                        behalfQuery.trim().length < 2
                          ? "Type at least 2 characters"
                          : "No matches"
                      }
                      getOptionLabel={(u) => u.name}
                      isOptionEqualToValue={(a, b) => a.id === b.id}
                      onInputChange={(v) => {
                        // Only track the search text here — never write
                        // the raw typed string into the form value.
                        // "On behalf of" must reflect a real employee
                        // picked from the dropdown, otherwise a partial
                        // query (e.g. "As") would get saved and shown on
                        // the detail page. Clearing the input also clears
                        // any prior selection.
                        setBehalfQuery(v);
                        if (behalfChoice && v !== behalfChoice.name) {
                          setBehalfChoice(null);
                          field.onChange("");
                        }
                      }}
                      onChange={(u) => {
                        setBehalfChoice(u);
                        field.onChange(u ? u.name : "");
                        if (!u) setBehalfQuery("");
                      }}
                    />
                  )}
                />
              </Box>
            </Stack>

            <Divider sx={{ my: 3 }} />
            <Box mb={1.75}>
              <SubSectionLabel>Attachments</SubSectionLabel>
            </Box>

            <Box data-tour="new-suggestion-attachments">
              <AttachmentUploader
                files={attachments}
                onChange={setAttachments}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: "block" }}
              >
                JPG, PNG, PDF, DOCX up to 10 MB each
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Spacer so the fixed footer never covers the bottom of the
            form's content. Height matches the footer's vertical box. */}
        <Box sx={{ height: 72 }} />

        {/* Fixed action footer — mirrors the AppBar (white bg, divider,
            spans content area to the right of the sidebar drawer).
            Submit stays disabled until yup says the form is valid. */}
        <Box
          className="no-print"
          sx={{
            position: "fixed",
            bottom: 0,
            left: { xs: 0, md: `${DRAWER_WIDTH}px` },
            right: 0,
            zIndex: (theme) => theme.zIndex.appBar,
            px: { xs: 2, md: 3 },
            py: 1.5,
            bgcolor: "background.paper",
            borderTop: "1px solid",
            borderColor: "divider",
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
          }}
        >
          <Button
            onClick={() => navigate("/suggestions")}
            sx={{
              borderRadius: 999,
              textTransform: "uppercase",
              letterSpacing: "0.4px",
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={<RavenIcon icon={Check} />}
            disabled={!isValid || isSubmitting || mutation.isPending}
            data-tour="new-suggestion-submit"
            sx={{
              borderRadius: 999,
              textTransform: "uppercase",
              letterSpacing: "0.4px",
            }}
          >
            {mutation.isPending ? "Submitting…" : "Submit"}
          </Button>
        </Box>
      </form>
    </Box>
  );
}
